/**
 * Citation Hover Card - Visual Ghost Preview
 *
 * Enterprise-grade citation preview that appears on hover:
 * - Shows visual snapshot of the cited region
 * - Displays computational provenance for calculations
 * - Shows confidence badge and verification type
 * - "Go to Source" button to jump to exact location
 */

import React, { useState, useEffect } from 'react';

import {
  FileText,
  Table,
  Presentation,
  FileSpreadsheet,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Calculator,
  Link2,
  Eye,
  Copy,
  Check,
  X,
} from 'lucide-react';

// Confidence badge configuration
const CONFIDENCE_CONFIG = {
  direct_quote: {
    label: 'Verbatim Source',
    color: 'emerald',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    textColor: 'text-emerald-700 dark:text-emerald-300',
    icon: CheckCircle2,
    iconColor: 'text-emerald-500',
  },
  paraphrase: {
    label: 'Summarized',
    color: 'blue',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    textColor: 'text-blue-700 dark:text-blue-300',
    icon: FileText,
    iconColor: 'text-blue-500',
  },
  calculation: {
    label: 'Computed Value',
    color: 'violet',
    bgColor: 'bg-violet-50 dark:bg-violet-900/20',
    borderColor: 'border-violet-200 dark:border-violet-800',
    textColor: 'text-violet-700 dark:text-violet-300',
    icon: Calculator,
    iconColor: 'text-violet-500',
  },
  composite: {
    label: 'Cross-File',
    color: 'amber',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-200 dark:border-amber-800',
    textColor: 'text-amber-700 dark:text-amber-300',
    icon: Link2,
    iconColor: 'text-amber-500',
  },
  inference: {
    label: 'Inferred',
    color: 'orange',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    borderColor: 'border-orange-200 dark:border-orange-800',
    textColor: 'text-orange-700 dark:text-orange-300',
    icon: AlertCircle,
    iconColor: 'text-orange-500',
  },
};

// File type icons
const FILE_ICONS = {
  pdf: FileText,
  excel: FileSpreadsheet,
  csv: Table,
  word: FileText,
  powerpoint: Presentation,
  document: FileText,
};

/**
 * Citation Hover Card Component
 */
export function CitationHoverCard({
  citation,
  onGoToSource,
  onCopy,
  isLoading = false,
  isPinned = false,
  onClose,
}) {
  const [copied, setCopied] = useState(false);
  const [snapshotHtml, setSnapshotHtml] = useState(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);

  // Get configuration based on verification type
  const verificationType = citation?.verification?.type || citation?.display?.type || 'inference';
  const config = CONFIDENCE_CONFIG[verificationType] || CONFIDENCE_CONFIG.inference;

  // ═══════════════════════════════════════════════════════════════════════
  // ✅ WAVE 25 FIX: Robust confidence calculation - handles both 0-1 and 0-100 formats
  // ═══════════════════════════════════════════════════════════════════════
  const rawConfidence =
    citation?.display?.confidence ??
    citation?.verification?.confidenceScore ??
    citation?.verification?.confidence ??
    0.7;
  // If value > 1, it's already a percentage; if <= 1, multiply by 100
  const normalizedConfidence = rawConfidence > 1 ? rawConfidence : Math.round(rawConfidence * 100);
  const confidencePercent = Math.min(100, Math.max(0, normalizedConfidence)); // Cap at 100%

  // ✅ PHASE 2: Embedding verification details (if present)
  const embeddingSim =
    citation?.verification?.embeddingSimilarity ??
    citation?.verification?.semanticSimilarity ??
    citation?.citationVerification?.similarity ??
    null;
  const embeddingThreshold = citation?.verification?.threshold ?? citation?.citationVerification?.threshold ?? 0.7;
  const embeddingValid =
    citation?.verification?.valid ??
    citation?.citationVerification?.valid ??
    null;

  // Get file icon
  const fileType = citation?.sourceFile?.type || citation?.display?.fileType || 'document';
  const FileIcon = FILE_ICONS[fileType] || FileText;

  // ✅ Build smart location string based on file type
  const buildLocationString = () => {
    // First check display.locationString (already formatted by backend)
    if (
      citation?.display?.locationString &&
      !citation.display.locationString.includes('undefined')
    ) {
      return citation.display.locationString;
    }

    const loc = citation?.location || {};

    // Excel/CSV: Show Sheet + Cells
    if (fileType === 'excel' || fileType === 'csv') {
      const parts = [];
      if (loc.sheet || loc.sheetName) parts.push(`Sheet "${loc.sheet || loc.sheetName}"`);
      if (loc.cellRange) parts.push(`Cells ${loc.cellRange}`);
      else if (loc.cellStart) parts.push(`Cell ${loc.cellStart}`);
      else if (loc.row) parts.push(`Row ${loc.row}`);
      return parts.length > 0 ? parts.join(' • ') : 'Spreadsheet Data';
    }

    // PDF/Word: Show Page
    if (loc.page) return `Page ${loc.page}`;

    // PowerPoint: Show Slide
    if (loc.slide) return `Slide ${loc.slide}`;

    // Audio/Video: Show timestamp
    if (loc.audioStart !== undefined) {
      return `${loc.audioStart}s - ${loc.audioEnd || loc.audioStart + 10}s`;
    }

    return 'Document';
  };

  // Load snapshot HTML if available
  useEffect(() => {
    if (citation?.snapshot?.htmlSnippet) {
      setSnapshotHtml(citation.snapshot.htmlSnippet);
    } else if (citation?.snapshot?.snapshotUrl && !snapshotHtml) {
      // Lazy load snapshot
      setLoadingSnapshot(true);
      fetch(citation.snapshot.snapshotUrl)
        .then(res => res.text())
        .then(html => setSnapshotHtml(html))
        .catch(() => setSnapshotHtml(null))
        .finally(() => setLoadingSnapshot(false));
    }
  }, [citation]);

  // Handle copy
  const handleCopy = () => {
    const textToCopy = citation?.content?.exactText || '';
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.(citation);
  };

  // Handle go to source
  const handleGoToSource = () => {
    onGoToSource?.(citation);
  };

  if (!citation) return null;

  const IconComponent = config.icon;

  return (
    <div
      className={`
      w-[380px] max-w-[90vw] rounded-xl shadow-xl border 
      ${config.bgColor} ${config.borderColor}
      overflow-hidden transition-all duration-200
    `}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-inherit bg-white/50 dark:bg-black/20">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`p-1.5 rounded-lg ${config.bgColor}`}>
              <IconComponent className={`h-4 w-4 ${config.iconColor}`} />
            </div>
            <div className="min-w-0">
              <div className={`text-xs font-medium ${config.textColor}`}>{config.label}</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                {citation?.sourceFile?.name || citation?.display?.fileName || 'Document'}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Confidence badge */}
            <div
              className={`
              px-2 py-1 rounded-full text-xs font-semibold
              ${config.bgColor} ${config.textColor}
            `}
            >
              {confidencePercent}%
            </div>

            {/* ✅ Close button when pinned */}
            {isPinned && onClose && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onClose();
                }}
                className="p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* ✅ PHASE 2: Embedding verification banner */}
        {typeof embeddingSim === 'number' && (
          <div className="mt-2 text-[11px] text-slate-600 dark:text-slate-300 flex items-center gap-2">
            <span className="font-medium">Embedding check:</span>
            <span>
              sim {Math.round(embeddingSim * 100)}% (min {Math.round(embeddingThreshold * 100)}%)
            </span>
            {embeddingValid === true ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                valid
              </span>
            ) : embeddingValid === false ? (
              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                <X className="h-3.5 w-3.5" />
                rejected
              </span>
            ) : null}
          </div>
        )}
      </div>

      {/* Visual Ghost Snapshot */}
      {(snapshotHtml || loadingSnapshot) && (
        <div className="px-4 py-3 border-b border-inherit bg-white dark:bg-slate-900">
          {loadingSnapshot ? (
            <div className="h-24 flex items-center justify-center">
              <div className="animate-spin h-5 w-5 border-2 border-slate-300 border-t-slate-600 rounded-full" />
            </div>
          ) : (
            <div
              className="max-h-[200px] overflow-auto rounded-lg"
              dangerouslySetInnerHTML={{ __html: snapshotHtml }}
            />
          )}
        </div>
      )}

      {/* Content Preview */}
      <div className="px-4 py-3">
        {/* Location - ✅ Fixed for Excel: Show Sheet + Cells */}
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-2">
          <FileIcon className="h-3.5 w-3.5" />
          <span>{buildLocationString()}</span>
        </div>

        {/* ✅ Excel/CSV: Show Cell References prominently */}
        {(fileType === 'excel' || fileType === 'csv') &&
          (citation?.location || citation?.computation) && (
            <div className="mb-3 p-2.5 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-lg border border-emerald-100 dark:border-emerald-800/50">
              <div className="text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-2 flex items-center gap-1.5">
                <Table className="h-3.5 w-3.5" />
                Data Source
              </div>

              {/* Sheet Name */}
              {(citation?.location?.sheet || citation?.location?.sheetName) && (
                <div className="mb-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Sheet:{' '}
                  </span>
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    {citation.location.sheet || citation.location.sheetName}
                  </span>
                </div>
              )}

              {/* Cell Range */}
              {(citation?.location?.cellRange || citation?.location?.cellStart) && (
                <div className="mb-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Cells:{' '}
                  </span>
                  <code className="text-xs font-mono bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded text-emerald-600 dark:text-emerald-400">
                    {citation.location.cellRange || citation.location.cellStart}
                  </code>
                </div>
              )}

              {/* Row/Column info */}
              {(citation?.location?.row || citation?.location?.column) && (
                <div className="mb-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Location:{' '}
                  </span>
                  <span className="text-xs text-emerald-600 dark:text-emerald-400">
                    {citation.location.row ? `Row ${citation.location.row}` : ''}
                    {citation.location.row && citation.location.column ? ', ' : ''}
                    {citation.location.column ? `Col ${citation.location.column}` : ''}
                  </span>
                </div>
              )}

              {/* Sample Cells Used */}
              {citation?.location?.sampleCells?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                    Sample Cells
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {citation.location.sampleCells.slice(0, 6).map((cell, i) => (
                      <span
                        key={i}
                        className="text-[10px] bg-emerald-100 dark:bg-emerald-800/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded font-mono"
                      >
                        {cell.address}:{' '}
                        {typeof cell.value === 'number' ? cell.value.toLocaleString() : cell.value}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulas */}
              {citation?.location?.formulas?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-emerald-100 dark:border-emerald-800/50">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                    Formula
                  </div>
                  {citation.location.formulas.slice(0, 2).map((f, i) => (
                    <code
                      key={i}
                      className="block text-[11px] font-mono bg-white dark:bg-slate-800 px-2 py-1 rounded text-violet-600 dark:text-violet-400 mb-1"
                    >
                      {f.cell} = {f.formula} → {f.result}
                    </code>
                  ))}
                </div>
              )}
            </div>
          )}

        {/* Exact text */}
        <blockquote className="text-sm text-slate-700 dark:text-slate-300 italic border-l-2 border-slate-200 dark:border-slate-700 pl-3 mb-3 line-clamp-4">
          "{citation?.content?.exactText || 'No text available'}"
        </blockquote>

        {/* Computational Provenance (for calculations) - ✅ Show for Excel too */}
        {(citation?.computation?.type ||
          citation?.computation?.formula ||
          citation?.computation?.inputCells?.length > 0) && (
          <div className="mt-3 p-3 bg-violet-50/50 dark:bg-violet-900/10 rounded-lg border border-violet-100 dark:border-violet-800/50">
            <div className="text-xs font-medium text-violet-700 dark:text-violet-300 mb-2 flex items-center gap-1.5">
              <Calculator className="h-3.5 w-3.5" />
              Calculation Logic
            </div>

            {citation.computation.formula && (
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                  Formula
                </div>
                <code className="text-xs bg-white dark:bg-slate-800 px-2 py-1 rounded border border-violet-100 dark:border-violet-800/50 font-mono">
                  {citation.computation.formula}
                </code>
              </div>
            )}

            {citation.computation.inputCells?.length > 0 && (
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                  Cells Used
                </div>
                <div className="flex flex-wrap gap-1">
                  {citation.computation.inputCells.map((cell, i) => (
                    <span
                      key={i}
                      className="text-[11px] bg-violet-100 dark:bg-violet-800/30 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded font-mono"
                    >
                      {cell}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {citation.computation.outputValue !== undefined && (
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-violet-100 dark:border-violet-800/50">
                <span className="text-[10px] uppercase tracking-wide text-slate-500">Result:</span>
                <span className="text-sm font-semibold text-violet-700 dark:text-violet-300">
                  {typeof citation.computation.outputValue === 'number'
                    ? citation.computation.outputValue.toLocaleString()
                    : citation.computation.outputValue}
                </span>
              </div>
            )}

            {citation.computation.pythonCode && (
              <div className="mt-2 pt-2 border-t border-violet-100 dark:border-violet-800/50">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                  Python Logic
                </div>
                <pre className="text-[11px] bg-slate-900 text-cyan-300 p-2 rounded font-mono overflow-x-auto">
                  {citation.computation.pythonCode}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Composite Sources (for cross-file citations) */}
        {verificationType === 'composite' && citation?.composite?.sources?.length > 0 && (
          <div className="mt-3 p-3 bg-amber-50/50 dark:bg-amber-900/10 rounded-lg border border-amber-100 dark:border-amber-800/50">
            <div className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5" />
              Sources Combined
            </div>

            <div className="space-y-2">
              {citation.composite.sources.map((src, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-800/30 text-amber-700 dark:text-amber-300 flex items-center justify-center text-[10px] font-medium">
                    {i + 1}
                  </span>
                  <span className="text-slate-600 dark:text-slate-300 truncate">
                    {src.fileName || 'File'}
                  </span>
                  {src.value && (
                    <span className="ml-auto font-mono text-amber-600 dark:text-amber-400">
                      {src.value}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {citation.composite.relationship && (
              <div className="mt-2 pt-2 border-t border-amber-100 dark:border-amber-800/50 text-xs text-amber-600 dark:text-amber-400">
                Relationship:{' '}
                <span className="font-medium capitalize">{citation.composite.relationship}</span>
                {citation.composite.diffValue && (
                  <span className="ml-2 font-mono">({citation.composite.diffValue})</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-2.5 border-t border-inherit bg-white/50 dark:bg-black/20 flex items-center justify-between">
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-500" />
              <span className="text-emerald-500">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              <span>Copy text</span>
            </>
          )}
        </button>

        <button
          onClick={handleGoToSource}
          className={`
            flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
            ${config.bgColor} ${config.textColor}
            hover:opacity-80 transition-opacity
          `}
        >
          <Eye className="h-3.5 w-3.5" />
          View Source
          <ExternalLink className="h-3 w-3 ml-0.5" />
        </button>
      </div>
    </div>
  );
}

export default CitationHoverCard;
